<template>
  <div style="position: relative; display: inline-block;">
    <div class="reportrange-text" @click="togglePicker">
      <slot name="input">
        <span class="margin-right-20">{{startText}} - {{endText}}</span>
        <a href="#" class="btn btn-sm m-btn m-btn--icon m-btn--icon-only m-btn--custom m-btn--pill"
           :class="`btn-ls`">
          <i class="la la-angle-down"></i>
        </a>
      </slot>
    </div>
    <transition name="slide-fade" mode="out-in">
      <div class="daterangepicker dropdown-menu ltr" :class="pickerStyles()" v-if="open"
           v-on-clickaway="clickAway">

        <div class="calendar left">
          <div class="daterangepicker_input m--hidden-mobile">
            <input class="input-mini form-control" type="text" name="daterangepicker_start"
                   :value="startText"
                   @blur="dateStartInput($event.target.value)"
                   @keydown.enter="dateStartInput($event.target.value, true)"/>
            <i class="fa fa-calendar glyphicon glyphicon-calendar"></i>
          </div>
          <div class="calendar-table">
            <calendar :monthDate="monthDate"
                      :locale="locale"
                      :start="start" :end="end"
                      @nextMonth="nextMonth" @prevMonth="prevMonth"
                      @dateClick="dateClick" @hoverDate="hoverDate"
            ></calendar>
          </div>

          <calendar-ranges :canSelect="in_selection" @clickCancel="open=false" @clickRange="clickRange"
                           @clickApply="clickedApply" :ranges="rangesForComponent" class="m--hidden-desktop m--hidden-tablet">
          </calendar-ranges>
        </div>

        <div class="calendar right m--hidden-mobile">
          <div class="daterangepicker_input">
            <input class="input-mini form-control" type="text" name="daterangepicker_end"
                   @blur="dateEndInput($event.target.value)"
                   @keydown.enter="dateEndInput($event.target.value, true)"
                   :value="endText"/>
            <i class="fa fa-calendar glyphicon glyphicon-calendar"></i>
          </div>
          <div class="calendar-table">
            <calendar :monthDate="nextMonthDate"
                      :locale="locale"
                      :start="start" :end="end"
                      @nextMonth="nextMonth" @prevMonth="prevMonth"
                      @dateClick="dateClick" @hoverDate="hoverDate"
            ></calendar>
          </div>
        </div>

        <calendar-ranges :canSelect="in_selection" @clickCancel="open=false" @clickRange="clickRange"
                         @clickApply="clickedApply" :ranges="rangesForComponent" class="m--hidden-mobile">
        </calendar-ranges>
      </div>
    </transition>
  </div>
</template>

<script>
  import moment from 'moment'
  // ensure these locales are bundled/available at runtime
  import 'moment/locale/pl'
  import 'moment/locale/en-gb'

  // Ensure 'en' is defined (use en-gb config as source) so moment.locale('en') works
  if (!moment.locales().includes('en')) {
    try {
      const enGbConfig = moment.localeData('en-gb')._config || moment.localeData('en-gb')
      moment.defineLocale('en', enGbConfig)
    } catch (e) {
      // fallback minimal mapping
      moment.defineLocale('en', {
        monthsShort: moment.localeData('en-gb') ? moment.localeData('en-gb').monthsShort() : this.$t('calendar.monthsShort'),
        weekdaysMin: moment.localeData('en-gb') ? moment.localeData('en-gb').weekdaysMin() : this.$t('calendar.daysShort'),
      })
    }
  }
  import Calendar from './Calendar'
  import CalendarRanges from './CalendarRanges'
  import {nextMonth, prevMonth} from './util'
  import {mixin as clickaway} from 'vue-clickaway'
  import {mapState} from 'vuex'
  import CalendarLocaleMixin from '@/mixins/CalendarLocaleMixin'

  export default {
    components: {Calendar, CalendarRanges},
    mixins: [clickaway, CalendarLocaleMixin],
    props: {
      localeData: {
        type: Object,
        default() {
          return {}
        },
      },
      startDate: {
        default() {
          return new Date()
        }
      },
      endDate: {
        default() {
          return new Date()
        }
      },
      ranges: {
        type: Object,
        default() {
          return {
            todayKey: [moment(), moment()],
            yesterdayKey: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
            thisMonthKey: [moment().startOf('month'), moment().endOf('month')],
            thisYearKey: [moment().startOf('year'), moment().endOf('year')],
            lastWeekKey: [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
            lastMonthKey: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
          }
        }
      },
      opens: {
        type: String,
        default: 'center'
      }
    },
    data() {
      const selectedLocale = (this.$i18n && this.$i18n.locale) || (this.localeData && this.localeData.locale) || 'en'
      const localeShort = String(selectedLocale).split(/[-_]/)[0]
      const localeToUse = localeShort === 'en' ? 'en-gb' : localeShort

      moment.locale(localeToUse)
      const mLocaleData = moment.localeData(localeToUse)

      let default_locale = {
        direction: 'ltr',
        format: mLocaleData.longDateFormat('L'),
        separator: ' - ',
        applyLabel: this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        weekLabel: 'W',
        customRangeLabel: this.$t('Custom Range'),
        daysOfWeek: mLocaleData.weekdaysMin(),
        monthNames: mLocaleData.monthsShort(),
        firstDay: mLocaleData.firstDayOfWeek(),
        todayKey: this.$t('Today'),
        yesterdayKey: this.$t('Yesterday'),
        thisMonthKey: this.$t('This month'),
        thisYearKey: this.$t('This year'),
        lastWeekKey: this.$t('Last week'),
        lastMonthKey: this.$t('Last month'),
      }

      let data = {locale: {...default_locale, ...this.localeData}}

      data.currentLocale = localeToUse

      data.monthDate = new Date(this.startDate)
      data.start = new Date(this.startDate)
      data.end = new Date(this.endDate)
      data.in_selection = false
      data.open = false

      // update day names order to firstDay
      if (data.locale.firstDay !== 0) {
        let iterator = data.locale.firstDay
        while (iterator > 0) {
          data.locale.daysOfWeek.push(data.locale.daysOfWeek.shift())
          iterator--
        }
      }
      return data
    },
    methods: {
      updateLocaleFromI18n(i18nLocale) {
        const selectedLocale = (i18nLocale) || (this.localeData && this.localeData.locale) || (this.$i18n && this.$i18n.locale) || 'en'
        const localeShort = String(selectedLocale).split(/[-_]/)[0]
        const localeToUse = localeShort === 'en' ? 'en-gb' : localeShort

        moment.locale(localeToUse)
        const mLocaleData = moment.localeData(localeToUse)

        this.currentLocale = localeToUse

        if (!this.locale) this.locale = {}
        this.locale.format = mLocaleData.longDateFormat('L')
        this.locale.daysOfWeek = mLocaleData.weekdaysMin()
        this.locale.monthNames = mLocaleData.monthsShort()
        this.locale.firstDay = mLocaleData.firstDayOfWeek()

        this.locale.applyLabel = this.$t('Apply');
        this.locale.cancelLabel = this.$t('Cancel');
        this.locale.customRangeLabel = this.$t('Custom Range');
        this.locale.todayKey = this.$t('Today');
        this.locale.yesterdayKey = this.$t('Yesterday');
        this.locale.thisMonthKey = this.$t('This month');
        this.locale.thisYearKey = this.$t('This year');
        this.locale.lastWeekKey = this.$t('Last week');
        this.locale.lastMonthKey = this.$t('Last month');
        // ensure correct order of days according to firstDay
        if (this.locale.firstDay !== 0 && Array.isArray(this.locale.daysOfWeek)) {
          // rotate to correct first day
          const days = this.locale.daysOfWeek.slice()
          for (let i = 0; i < this.locale.firstDay; i++) {
            days.push(days.shift())
          }
          this.locale.daysOfWeek = days
        }
      },
      nextMonth() {
        this.monthDate = nextMonth(this.monthDate)
      },
      prevMonth() {
        this.monthDate = prevMonth(this.monthDate)
      },
      dateClick(value) {
        if (this.in_selection) {
          this.in_selection = false
          this.end = new Date(value)
          if (this.end < this.start) {
            this.in_selection = true
            this.start = new Date(value)
          }
        } else {
          this.in_selection = true
          this.start = new Date(value)
          this.end = new Date(value)
        }
      },
      hoverDate(value) {
        let dt = new Date(value)
        if (this.in_selection && dt > this.start)
          this.end = dt
      },
      togglePicker() {
        this.open = !this.open
      },
      pickerStyles() {
        let styles = {
          'show-calendar': this.open,
          opensright: this.opens === 'right',
          opensleft: this.opens === 'left',
          openscenter: this.opens === 'center'
        };

        styles[`daterangepicker-ls`] = true;
        return styles;
      },
      clickedApply() {
        this.open = false
        this.$emit('update', {startDate: this.start, endDate: this.end})
      },
      clickAway() {
        if (this.open) {
          this.open = false
        }
      },
      clickRange(value) {
        this.start = new Date(value[0])
        this.end = new Date(value[1])
        this.monthDate = new Date(value[0])
        this.clickedApply()
      },
      dateStartInput(inputValue, apply = false) {
        let date = moment(inputValue, this.calendarFormat);

        if (date.isValid() && date.isSameOrBefore(moment(this.end))) {
          this.start = date.toDate();
          this.monthDate = date.toDate();
        } else {
          this.$notify.warning(this.$t('Invalid dates'))
        }

        if (apply) {
          this.clickedApply();
        }
      },
      dateEndInput(inputValue, apply = false) {
        let date = moment(inputValue, this.calendarFormat);

        if (date.isValid() && date.isSameOrAfter(moment(this.start))) {
          this.end = date.toDate();
        } else {
          this.$notify.warning(this.$t('Invalid dates'))
        }

        if (apply) {
          this.clickedApply();
        }
      }
    },
    created() {
        this.updateLocaleFromI18n(this.$i18n && this.$i18n.locale)
    },
    mounted() {
      this.updateLocaleFromI18n(this.$i18n && this.$i18n.locale)
    },
    computed: {
      nextMonthDate() {
        return nextMonth(this.monthDate)
      },
      startText() {
        return moment(this.start).format(this.calendarFormat)
      },
      endText() {
        return moment(this.end).format(this.calendarFormat)
      },
      ...mapState({}),
      localizedRanges() {
        return {
          todayKey: this.$t('Today'),
          yesterdayKey: this.$t('Yesterday'),
          thisMonthKey: this.$t('This month'),
          thisYearKey: this.$t('This year'),
          lastWeekKey: this.$t('Last week'),
          lastMonthKey: this.$t('Last month'),
        }
      },
      rangesForComponent() {
        // Build an object with translated labels as keys and the original ranges as values.
        const keyMap = {
          todayKey: this.$t('Today'),
          yesterdayKey: this.$t('Yesterday'),
          thisMonthKey: this.$t('This month'),
          thisYearKey: this.$t('This year'),
          lastWeekKey: this.$t('Last week'),
          lastMonthKey: this.$t('Last month'),
        }

        const result = {}
        // If `this.ranges` is not an object, return it as-is to avoid breaking.
        if (!this.ranges || typeof this.ranges !== 'object') return this.ranges

        Object.keys(this.ranges).forEach(k => {
          const label = keyMap[k] || k
          // keep the original value (array of two moments/dates)
          result[label] = this.ranges[k]
        })

        return result
      }
    },
    watch: {
      '$i18n.locale'(val) {
        this.updateLocaleFromI18n(val)
      },
      startDate(value) {
        this.start = new Date(value)
      },
      endDate(value) {
        this.end = new Date(value)
      }
    }
  }

</script>

<style>
  .margin-right-20 {
    margin-right: 20px;
  }

  .reportrange-text {
    cursor: pointer;
    padding: 5px 10px;
    width: 100%;
  }

  .daterangepicker.show-calendar {
    display: inline-flex;
  }

  @media (min-width: 768px) {
    .daterangepicker .ranges {
      width: 160px;
    }

    .ranges {
      width: 130px;
    }

    .range_inputs {
      margin-bottom: 10px;
    }
  }

  @media (max-width: 768px) {
    .range_inputs {
      padding-left: 15px;
    }
  }

  .show-calendar {
    display: block;
    width: auto;
  }

  div.daterangepicker.opensleft {
    top: 45px;
    right: 10px;
    left: auto;
  }

  div.daterangepicker.openscenter {
    top: 45px;
    right: auto;
    left: 50%;
    transform: translate(-50%, 0);
  }

  div.daterangepicker.opensright {
    top: 45px;
    left: 10px;
    right: auto;
  }

  /* Enter and leave animations can use different */
  /* durations and timing functions.              */
  .slide-fade-enter-active {
    transition: all .2s ease;
  }

  .slide-fade-leave-active {
    transition: all .1s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .slide-fade-enter, .slide-fade-leave-to
    /* .slide-fade-leave-active for <2.1.8 */
  {
    transform: translateX(10px);
    opacity: 0;
  }

  .daterangepicker-ls tbody td.in-range, .daterangepicker-ls tbody td.active, .daterangepicker-ls tbody td.today {
    background-color: var(--accent-color) !important;
    color: var(--btn-color) !important;
  }

  .daterangepicker-ls tbody td.off.in-range {
    color: #c0c0c0 !important;
  }
</style>
